import { useState } from "react";
import {
  CartProductCardProps,
  UOM,
} from "../../features/createSecondaryOrder/CreateSecondaryOrderType";
import { ReactComponent as TrashIcon } from "../../assets/image/svg/trsahIcon.svg";
import dummyProductImage from "../../assets/image/png/dummyProduct.png";
import { OrderType } from "../../types/types";
import { getStockCountColors } from "./util";
import { performUomConversion } from "../../services/catalogueService";
import { updateMultiQtyInCart } from "../../features/createSecondaryOrder/CreateSecondaryOrderService";
import "./CartProductCard.css";
import { Alert, Snackbar } from "@mui/material";
import { truncateToTwoDecimals } from "../dms_common/DmsUtils";

const CartProductCard: React.FC<CartProductCardProps> = ({
  product,
  updateQuantityInCart,
  orderType,
  handleClickViewDetails,
  resetCart,
  totalProducts,
  customerInfo,
  setCart,
  catalogue,
  disableStockCheck,
  enableMOQCheck,
}) => {
  const {
    batchCode,
    caseQuantity,
    pieceQuantity,
    otherUnitQuantity,
    availableCaseQty,
    availablePieceQty,
    availableOtherQty,
    imageUrl,
    name,
    price,
    casePrice,
    otherUnitPrice,
    mrp,
    case2Piece,
    otherUnit2Piece,
    uom,
    caseMrp,
    casePtr,
    otherUnitName,
    minimumOrderQuantity = 0,
    pieceSize
  } = product;
  
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [caseQuantityInput, setCaseQuantityInput] = useState<string>(
    caseQuantity?.toString() || ""
  );
  const [pieceQuantityInput, setPieceQuantityInput] = useState<string>(
    pieceQuantity?.toString() || ""
  );
  const [otherUnitQuantityInput, setOtherUnitQuantityInput] = useState<string>(
    otherUnitQuantity?.toString() || ""
  );

  const [isEditingCaseQuantity, setIsEditingCaseQuantity] = useState<boolean>(false);
  const [isEditingPieceQuantity, setIsEditingPieceQuantity] = useState<boolean>(false);
  const [isEditingOtherUnitQuantity, setIsEditingOtherUnitQuantity] = useState<boolean>(false);

  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const {
    ptrLabel,
    ptdLabel,
    mrpLabel,
    unitLabel,
    caseUnitLabel,
    otherUnitLabel,
    packUnitLabel,
    currency,
    uomConversion,
    designationBasedUomSecondary,
    disableOtherUnitNameWiseCavinKair,
    displayOtherUnitName,
  } = ConfigDictionary;

  let customOtherUnitLabel = displayOtherUnitName
    ? (otherUnitName && otherUnitName.length > 0 ? otherUnitName : otherUnitLabel)
    : otherUnitLabel;

  const handleMultiQtyChange = (newCaseQty: number, newOtherQty: number, newPieceQty: number) => {
    const { convertedCaseQty, convertedOtherQty, convertedPieceQty, hasQtyExceededStock } =
      performUomConversion(
        newCaseQty,
        newOtherQty,
        newPieceQty,
        uom,
        case2Piece,
        otherUnit2Piece,
        availableCaseQty,
        availableOtherQty,
        availablePieceQty,
        undefined,
        otherUnitName
      );
    if (hasQtyExceededStock) setSnackbarOpen(true);
    setCaseQuantityInput(convertedCaseQty.toString());
    setOtherUnitQuantityInput(convertedOtherQty.toString());
    setPieceQuantityInput(convertedPieceQty.toString());
    updateMultiQtyInCart(
      batchCode,
      {
        caseQuantity: convertedCaseQty,
        otherUnitQuantity: convertedOtherQty,
        pieceQuantity: convertedPieceQty,
      },
      customerInfo,
      catalogue,
      setCart
    );
  };

  const handleIncOrDecQty = (type: UOM, change: number) => {
    if (uomConversion) {
      const newCaseQty = type === UOM.CASE ? +caseQuantityInput + change : +caseQuantityInput;
      const newOtherQty =
        type === UOM.OTHER ? +otherUnitQuantityInput + change : +otherUnitQuantityInput;
      const newPieceQty = type === UOM.PIECE ? +pieceQuantityInput + change : +pieceQuantityInput;
      handleMultiQtyChange(newCaseQty, newOtherQty, newPieceQty);
    } else {
      const newQty =
        type === UOM.CASE
          ? Math.max(+caseQuantityInput + change, 0).toString()
          : type === UOM.OTHER
          ? Math.max(+otherUnitQuantityInput + change, 0).toString()
          : Math.max(+pieceQuantityInput + change, 0).toString();
      type === UOM.CASE
        ? setCaseQuantityInput(newQty)
        : type === UOM.OTHER
        ? setOtherUnitQuantityInput(newQty)
        : setPieceQuantityInput(newQty);
      updateQuantityInCart(batchCode, +newQty, type, customerInfo, catalogue, setCart);
    }
  };

  const handleInputBlur = (type: UOM) => {
    if (uomConversion) {
      handleMultiQtyChange(+caseQuantityInput, +otherUnitQuantityInput, +pieceQuantityInput);
    } else {
      const newQty =
        type === UOM.CASE
          ? Math.min(+caseQuantityInput, availableCaseQty).toString()
          : type === UOM.OTHER
          ? Math.min(+otherUnitQuantityInput, availableOtherQty).toString()
          : Math.min(+pieceQuantityInput, availablePieceQty).toString();
      type === UOM.CASE
        ? setCaseQuantityInput(newQty)
        : type === UOM.OTHER
        ? setOtherUnitQuantityInput(newQty)
        : setPieceQuantityInput(newQty);
      updateQuantityInCart(batchCode, +newQty, type, customerInfo, catalogue, setCart);
    }
  };

  const isInStock = Boolean(availableCaseQty || availablePieceQty || availableOtherQty);
  const availableUoms = uom?.replace(/\s+/g, "").split(",");
  const disablePieceUom = (!availableUoms.includes("all") && !availableUoms.includes("ea"));
  const disableCaseUom = (!availableUoms.includes("all") && !availableUoms.includes("cs")) || (uomConversion && (product?.case2Piece === 0));
  const disableOtherUom = (!availableUoms.includes("all") && !availableUoms.includes("other")) || (uomConversion && (product?.otherUnit2Piece === 0)) || (disableOtherUnitNameWiseCavinKair && product?.otherUnitName?.trim().length === 0);

  const { bg, text, border } = getStockCountColors(isInStock);

  const checkIfNormalizedQtyMatchesOrExceedsStock = () => {
    const normalizedQty =
      +caseQuantityInput * case2Piece +
      +otherUnitQuantityInput * otherUnit2Piece +
      +pieceQuantityInput;
    const normalizedStock =
      availableCaseQty * case2Piece + availableOtherQty * otherUnit2Piece + availablePieceQty;
    return normalizedQty >= normalizedStock;
  };

  const isValidQuantity = (type: UOM) => {
    if (uomConversion) {
      return !checkIfNormalizedQtyMatchesOrExceedsStock();
    }
    if(disableStockCheck) return true;
    if (type === UOM.PIECE) {
      return availablePieceQty > +pieceQuantityInput;
    } else if (type === UOM.CASE) {
      return availableCaseQty > +caseQuantityInput;
    } else {
      return availableOtherQty > +otherUnitQuantityInput;
    }
  };
  const checkIfNormalizedQtyMatchesOrBelowMOQ = () => {
    const normalizedQty =
      +caseQuantityInput * case2Piece +
      +otherUnitQuantityInput * otherUnit2Piece +
      +pieceQuantityInput;
    return normalizedQty <= minimumOrderQuantity
  } 
  const isQuantityAboveOrEqualMOQ = () => {
    if (!enableMOQCheck) return true;
    return !checkIfNormalizedQtyMatchesOrBelowMOQ()
  }
  const currentTotalQuantity = () => {
    const normalizedQty =
      +caseQuantityInput * case2Piece +
      +otherUnitQuantityInput * otherUnit2Piece +
      +pieceQuantityInput;
    return normalizedQty
  }
  const makeQtyEqualsMOQ = () => {
    const { convertedCaseQty, convertedOtherQty, convertedPieceQty } = performUomConversion(0,0,minimumOrderQuantity,uom,case2Piece,otherUnit2Piece,0,0,0,true, otherUnitName);
    handleMultiQtyChange(convertedCaseQty, convertedOtherQty, convertedPieceQty)
  }

  const makeQtyEqualZero = () => {
    handleMultiQtyChange(0, 0, 0)
  }
  const handleAddClick = (uom: UOM, quantity: number) => {
    if (disableAddButton(uom)) return;
      if ( enableMOQCheck && currentTotalQuantity() + quantity < minimumOrderQuantity ) {
        makeQtyEqualsMOQ();
      } else {
        handleIncOrDecQty(uom, 1);
    }
  };
  const handleSubtractClick = (uom: UOM, quantity: number) => {
    if (
      enableMOQCheck &&
      currentTotalQuantity() - quantity < minimumOrderQuantity
    ) {
      makeQtyEqualZero();
    } else {
      handleIncOrDecQty(uom, -1);
    }
  };
  const disableAddButton = (type: UOM) => {
    if (
      (type === UOM.CASE && disableCaseUom) ||
      (type === UOM.OTHER && disableOtherUom) ||
      (type === UOM.PIECE && disablePieceUom)
    ) {
      return true;
    }
    if (enableMOQCheck && !disableStockCheck) {
      const normalizedStock =
        availableCaseQty * case2Piece +
        availableOtherQty * otherUnit2Piece +
        availablePieceQty;

      if (normalizedStock === 0 && minimumOrderQuantity === 0) {
        return true;
      }

      if (normalizedStock < minimumOrderQuantity) {
        return true;
      }

      if (uomConversion) {
        return checkIfNormalizedQtyMatchesOrExceedsStock();
      }
    }

    if (uomConversion) {
      return checkIfNormalizedQtyMatchesOrExceedsStock();
    }
    return ( !(disableStockCheck && disableStockCheck) && (
      (type === UOM.CASE && !availableCaseQty) ||
      (type === UOM.OTHER && !availableOtherQty) ||
      (type === UOM.PIECE && !availablePieceQty)
    ));
  };

  const getDisabledButtonStyling = () => {
    return {
      cursor: "not-allowed",
      backgroundColor: "#F4F4F4",
      color: "#7C7C7C",
    };
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleDeleteProduct = () => {
    // reset the cart DB while deleting last remaining product
    if (totalProducts === 1 && resetCart) {
      resetCart();
      return;
    }
    setCaseQuantityInput("");
    setOtherUnitQuantityInput("");
    setPieceQuantityInput("");
    updateMultiQtyInCart(
      batchCode,
      {
        caseQuantity: 0,
        otherUnitQuantity: 0,
        pieceQuantity: 0,
      },
      customerInfo,
      catalogue,
      setCart
    );
  };

  const designationEnabled  =  designationBasedUomSecondary?.enable ?? false;
  const availableDesignations = designationBasedUomSecondary?.roles?.designationKeys?.map(((item: { type: any; }) => item?.type)) || [];
  const currentDesignation = JSON.parse(localStorage.authContext).user.designation || [];

  const designation = availableDesignations.find((type: any) => 
    currentDesignation.includes(type)
  );
  
  const designationKeys = designationBasedUomSecondary?.roles?.designationKeys ?? [];
  const matchedRole = designationKeys.find((role: { type: string; uomAllowed: any }) => role.type === designation);


  if(designationEnabled && designation && designationKeys && matchedRole  && orderType === OrderType.SECONDARY) {
    return (
      <div className="CartProductCard">
        <Snackbar
          open={snackbarOpen}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          autoHideDuration={5000}
          onClose={handleSnackbarClose}
        >
          <Alert onClose={handleSnackbarClose} severity="warning" sx={{ width: "100%" }}>
            The entered quantity exceeds the available stock. Your quantity will be adjusted to match the available stock for this SKU.
          </Alert>
        </Snackbar>
        <div onClick={handleDeleteProduct} className="cart_remove_item">
          <TrashIcon width={12} height={15} />
        </div>
        <div className="CartProductCard_image_container">
          <img
            src={imageUrl || ConfigDictionary?.defaultCatalogueImage || dummyProductImage}
            alt={name}
            // style={{ width: imageUrl ? "95%" : "80%", height: imageUrl ? "95%" : "80%"}}
            style={{ maxWidth: "80px", maxHeight: "80px" }}
            onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
              const target = e.target as HTMLImageElement;
              target.onerror = null; // Prevent infinite loop
              target.src = dummyProductImage; // Fallback to dummy image
            }}
          />
        </div>
        <div className="CartProductCard_details_container">
          <div style={{ lineHeight: "1.4", marginBottom: "0.3rem" }}>
            <span style={{ fontWeight: 600 }}>{`${name}, ${pieceSize}`}</span>
            <span style={{ color: "#909090", marginLeft: "6px" }}>{`(${batchCode})`}</span>
          </div>
          {matchedRole.uomAllowed.case?.trim() && (<div style={{ color: "#676767" }}>
            {mrpLabel || "MRP"}* {currency} {caseMrp}
          </div>)}
          {matchedRole.uomAllowed.piece?.trim() && (<div style={{ color: "#676767" }}>
            {mrpLabel || "MRP"}* {currency} {mrp}
          </div>)}
          <div
            style={{
              color: "#227C9D",
              textDecoration: "underline",
              cursor: "pointer",
            }}
            onClick={() => handleClickViewDetails && handleClickViewDetails(product)}
          >
            View details
          </div>
        </div>
        <div className="CartProductCard_button_container_parent">
          <div className="CartProductCard_button_container">
            {matchedRole.uomAllowed.case?.trim() && (<div className="CartProductCard_uom_div">
              <div className="price_box">
                <span style={{ color: "#5C5C5C", fontWeight: 550 }}>
                  {currency}
                  {truncateToTwoDecimals(casePrice)}
                </span>
                <span style={{ color: "#7C7C7C", fontSize: "8px" }}>
                  /{caseUnitLabel?.toUpperCase()}
                </span>
              </div>
              {+caseQuantityInput === 0 && !isEditingCaseQuantity ? (
                <div
                  onClick={() => handleAddClick(UOM.CASE, case2Piece)}
                  className="add_and_unit_button"
                  style={disableAddButton(UOM.CASE) ? getDisabledButtonStyling() : {}}
                >
                  ADD
                  <span className="unit_text">
                    {case2Piece} {unitLabel?.toUpperCase()}
                    {case2Piece > 1 ? "S" : ""}
                  </span>
                </div>
              ) : (
                <div className="quantity_box">
                  <div onClick={() => handleSubtractClick(UOM.CASE, case2Piece)}
                   className="decrement_button">
                    <span className="increment_decrement_icon">-</span>
                  </div>
                  <div className="quantity_input_container">
                    <input
                      inputMode="numeric"
                      value={caseQuantityInput}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d{0,4}$/.test(value)) {
                          setCaseQuantityInput(value);
                        }
                      }}
                      onFocus={() => setIsEditingCaseQuantity(true)}
                      onBlur={() => {
                        setIsEditingCaseQuantity(false);
                        handleInputBlur(UOM.CASE);
                        if (!isQuantityAboveOrEqualMOQ()) {
                          makeQtyEqualsMOQ();
                        }
                      }}
                      className="quantity_input"
                    />
                  </div>
                  <div
                    onClick={() => isValidQuantity(UOM.CASE) && handleIncOrDecQty(UOM.CASE, 1)}
                    className="increment_button"
                    style={isValidQuantity(UOM.CASE) ? {} : getDisabledButtonStyling()}
                  >
                    <span
                      className="increment_decrement_icon"
                      style={isValidQuantity(UOM.CASE) ? {} : getDisabledButtonStyling()}
                    >
                      +
                    </span>
                  </div>
                </div>
              )}
            </div>) }
            
            
            { matchedRole.uomAllowed.piece?.trim() && (<div className="CartProductCard_uom_div">
                <div className="price_box">
                  <span style={{ color: "#5C5C5C", fontWeight: 550 }}>
                    {currency}
                    {truncateToTwoDecimals(price)}
                  </span>
                  <span style={{ color: "#7C7C7C", fontSize: "9px" }}>
                    /{packUnitLabel?.toUpperCase()}
                  </span>
                </div>
                {+pieceQuantityInput === 0 && !isEditingPieceQuantity ? (
                  <div
                    onClick={() => handleAddClick(UOM.PIECE, 1)}
                    className="add_and_unit_button"
                    style={disableAddButton(UOM.PIECE) ? getDisabledButtonStyling() : {}}
                  >
                    ADD
                    <span className="unit_text">1 {unitLabel?.toUpperCase()}</span>
                  </div>
                ) : (
                  <div className="quantity_box">
                    <div onClick={() => handleSubtractClick(UOM.PIECE, 1)}
                      className="decrement_button"
                    >
                      <span className="increment_decrement_icon">-</span>
                    </div>
                    <div className="quantity_input_container">
                      <input
                        inputMode="numeric"
                        value={pieceQuantityInput}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d{0,4}$/.test(value)) {
                            setPieceQuantityInput(value);
                          }
                        }}
                        onFocus={() => setIsEditingPieceQuantity(true)}
                        onBlur={() => {
                          setIsEditingPieceQuantity(false);
                          handleInputBlur(UOM.PIECE);
                          if (!isQuantityAboveOrEqualMOQ()) {
                            makeQtyEqualsMOQ();
                          }
                        }}
                        className="quantity_input"
                      />
                    </div>
                    <div
                      onClick={() => isValidQuantity(UOM.PIECE) && handleIncOrDecQty(UOM.PIECE, 1)}
                      className="increment_button"
                      style={isValidQuantity(UOM.PIECE) ? {} : getDisabledButtonStyling()}
                    >
                      <span
                        className="increment_decrement_icon"
                        style={isValidQuantity(UOM.PIECE) ? {} : getDisabledButtonStyling()}
                      >
                        +
                      </span>
                    </div>
                  </div>
                )}
            </div>)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="CartProductCard">
      <Snackbar
        open={snackbarOpen}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity="warning" sx={{ width: "100%" }}>
          The entered quantity exceeds the available stock. Your quantity will be adjusted to match the available stock for this SKU.
        </Alert>
      </Snackbar>
      <div onClick={handleDeleteProduct} className="cart_remove_item">
        <TrashIcon width={12} height={15} />
      </div>
      <div className="CartProductCard_image_container">
        <img
          src={imageUrl || ConfigDictionary?.defaultCatalogueImage || dummyProductImage}
          alt={name}
          style={{ maxWidth: "80px", maxHeight: "80px" }}
          onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
            const target = e.target as HTMLImageElement;
            target.onerror = null; // Prevent infinite loop
            target.src = dummyProductImage; // Fallback to dummy image
          }}
        />
      </div>
      <div className="CartProductCard_details_container">
        <div style={{ lineHeight: "1.4", marginBottom: "0.3rem" }}>
          <span style={{ fontWeight: 600 }}>{`${name}, ${pieceSize}`}</span>
          <span style={{ color: "#909090", marginLeft: "6px" }}>{`(${batchCode})`}</span>
        </div>
        <div style={{ fontWeight: 600 }}>
          {orderType === OrderType.PRIMARY ? ptdLabel || "PTD" : ptrLabel || "PTR"}* {currency}{" "}
          {truncateToTwoDecimals(price)}
        </div>
        <div style={{ color: "#676767" }}>
          {mrpLabel || "MRP"}* {currency} {mrp}
        </div>
        <div
          style={{
            color: "#227C9D",
            textDecoration: "underline",
            cursor: "pointer",
          }}
          onClick={() => handleClickViewDetails && handleClickViewDetails(product)}
        >
          View details
        </div>
      </div>
      <div className="CartProductCard_button_container_parent" style={{width: "52%"}}>
        <div className="CartProductCard_button_container">
          <div className="CartProductCard_uom_div">
            <div className="price_box">
              <span style={{ color: "#5C5C5C", fontWeight: 550 }}>
                {currency}
                {truncateToTwoDecimals(casePrice)}
              </span>
              <span style={{ color: "#7C7C7C", fontSize: "8px" }}>
                /{caseUnitLabel?.toUpperCase()}
              </span>
            </div>
            {+caseQuantityInput === 0 && !isEditingCaseQuantity ? (
              <div
                onClick={() => handleAddClick(UOM.CASE, case2Piece)}
                className="add_and_unit_button"
                style={disableAddButton(UOM.CASE) ? getDisabledButtonStyling() : {}}
              >
                ADD
                <span className="unit_text">
                  {case2Piece} {unitLabel?.toUpperCase()}
                  {case2Piece > 1 ? "S" : ""}
                </span>
              </div>
            ) : (
              <div className="quantity_box">
                <div onClick={() => handleSubtractClick(UOM.CASE, case2Piece)}
                  className="decrement_button"
                >
                  <span className="increment_decrement_icon">-</span>
                </div>
                <div className="quantity_input_container">
                  <input
                    inputMode="numeric"
                    value={caseQuantityInput}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d{0,4}$/.test(value)) {
                        setCaseQuantityInput(value);
                      }
                    }}
                    onFocus={() => setIsEditingCaseQuantity(true)}
                    onBlur={() => {
                      setIsEditingCaseQuantity(false);
                      handleInputBlur(UOM.CASE);
                      if (!isQuantityAboveOrEqualMOQ()) {
                        makeQtyEqualsMOQ();
                      }
                    }}
                    className="quantity_input"
                  />
                </div>
                <div
                  onClick={() => isValidQuantity(UOM.CASE) && handleIncOrDecQty(UOM.CASE, 1)}
                  className="increment_button"
                  style={isValidQuantity(UOM.CASE) ? {} : getDisabledButtonStyling()}
                >
                  <span
                    className="increment_decrement_icon"
                    style={isValidQuantity(UOM.CASE) ? {} : getDisabledButtonStyling()}
                  >
                    +
                  </span>
                </div>
              </div>
            )}
          </div>
          {orderType === OrderType.SECONDARY && otherUnitLabel && (
            <div className="CartProductCard_uom_div">
              <div className="price_box">
                <span style={{ color: "#5C5C5C", fontWeight: 550 }}>
                  {currency}
                  {truncateToTwoDecimals(otherUnitPrice)}
                </span>
                <span style={{ color: "#7C7C7C", fontSize: "9px" }}>
                  /{customOtherUnitLabel?.toUpperCase()?.slice(0, 3)}
                </span>
              </div>
              {+otherUnitQuantityInput === 0 && !isEditingOtherUnitQuantity ? (
                <div
                  onClick={() => handleAddClick(UOM.OTHER, otherUnit2Piece)}
                  className="add_and_unit_button"
                  style={disableAddButton(UOM.OTHER) ? getDisabledButtonStyling() : {}}
                >
                  ADD
                  <span className="unit_text">
                    {otherUnit2Piece} {unitLabel?.toUpperCase()}
                    {otherUnit2Piece > 1 ? "S" : ""}
                  </span>
                </div>
              ) : (
                <div className="quantity_box">
                  <div onClick={() => handleSubtractClick(UOM.OTHER, otherUnit2Piece)}
                    className="decrement_button"
                  >
                    <span className="increment_decrement_icon">-</span>
                  </div>
                  <div className="quantity_input_container">
                    <input
                      inputMode="numeric"
                      value={otherUnitQuantityInput}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d{0,4}$/.test(value)) {
                          setOtherUnitQuantityInput(value);
                        }
                      }}
                      onFocus={() => setIsEditingOtherUnitQuantity(true)}
                      onBlur={() => {
                        setIsEditingOtherUnitQuantity(false);
                        handleInputBlur(UOM.OTHER);
                        if (!isQuantityAboveOrEqualMOQ()) {
                          makeQtyEqualsMOQ();
                        }
                      }}
                      className="quantity_input"
                    />
                  </div>
                  <div
                    onClick={() => isValidQuantity(UOM.OTHER) && handleIncOrDecQty(UOM.OTHER, 1)}
                    className="increment_button"
                    style={isValidQuantity(UOM.OTHER) ? {} : getDisabledButtonStyling()}
                  >
                    <span
                      className="increment_decrement_icon"
                      style={isValidQuantity(UOM.OTHER) ? {} : getDisabledButtonStyling()}
                    >
                      +
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
          {orderType === OrderType.SECONDARY && (
            <div className="CartProductCard_uom_div">
              <div className="price_box">
                <span style={{ color: "#5C5C5C", fontWeight: 550 }}>
                  {currency}
                  {truncateToTwoDecimals(price)}
                </span>
                <span style={{ color: "#7C7C7C", fontSize: "9px" }}>
                  /{packUnitLabel?.toUpperCase()}
                </span>
              </div>
              {+pieceQuantityInput === 0 && !isEditingPieceQuantity ? (
                <div
                  onClick={() => handleAddClick(UOM.PIECE, 1)}
                  className="add_and_unit_button"
                  style={disableAddButton(UOM.PIECE) ? getDisabledButtonStyling() : {}}
                >
                  ADD
                  <span className="unit_text">1 {unitLabel?.toUpperCase()}</span>
                </div>
              ) : (
                <div className="quantity_box">
                  <div onClick={() => handleSubtractClick(UOM.PIECE, 1)}
                    className="decrement_button"
                  >
                    <span className="increment_decrement_icon">-</span>
                  </div>
                  <div className="quantity_input_container">
                    <input
                      inputMode="numeric"
                      value={pieceQuantityInput}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d{0,4}$/.test(value)) {
                          setPieceQuantityInput(value);
                        }
                      }}
                      onFocus={() => setIsEditingPieceQuantity(true)}
                      onBlur={() => {
                        setIsEditingPieceQuantity(false);
                        handleInputBlur(UOM.PIECE);
                        if (!isQuantityAboveOrEqualMOQ()) {
                          makeQtyEqualsMOQ();
                        }
                      }}
                      className="quantity_input"
                    />
                  </div>
                  <div
                    onClick={() => isValidQuantity(UOM.PIECE) && handleIncOrDecQty(UOM.PIECE, 1)}
                    className="increment_button"
                    style={isValidQuantity(UOM.PIECE) ? {} : getDisabledButtonStyling()}
                  >
                    <span
                      className="increment_decrement_icon"
                      style={isValidQuantity(UOM.PIECE) ? {} : getDisabledButtonStyling()}
                    >
                      +
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartProductCard;
